// frontend/src/services/wallet.api.js

import axios from '../utils/axiosConfig';

const walletAPI = {
  // ============================================
  // EXISTING METHODS
  // ============================================

  getWallet: async () => {
    const response = await axios.get('/wallet');
    return response.data;
  },

  getTransactions: async (params) => {
    const response = await axios.get('/wallet/transactions', { params });
    return response.data;
  },

  fundWallet: async (amount) => {
    const response = await axios.post('/wallet/fund', { amount });
    return response.data;
  },

  verifyFunding: async (reference) => {
    const response = await axios.post('/wallet/fund/verify', { reference });
    return response.data;
  },

  getTransactionSummary: async () => {
    const response = await axios.get('/wallet/summary');
    return response.data;
  },

  getBanks: async () => {
    const response = await axios.get('/wallet/banks');
    return response.data;
  },

  // ============================================
  // LEGACY BANK TRANSFER METHODS (Keep for compatibility)
  // ============================================

  getVirtualAccount: async () => {
    const response = await axios.get('/wallet/virtual-account');
    return response.data;
  },

  generateBankTransfer: async (amount) => {
    const response = await axios.post('/wallet/bank-transfer/generate', { amount });
    return response.data;
  },

  verifyBankTransfer: async (reference) => {
    const response = await axios.post('/wallet/bank-transfer/verify', { reference });
    return response.data;
  },

  getPendingTransfers: async () => {
    const response = await axios.get('/wallet/bank-transfer/pending');
    return response.data;
  },

  // ============================================
  // V2 VIRTUAL ACCOUNT METHODS (Monnify)
  // ============================================

  /**
   * Create or get virtual account for the authenticated user
   * Requires BVN or NIN (at least one)
   * 
   * @param {string} bvn - 11-digit BVN (optional)
   * @param {string} nin - 11-digit NIN (optional)
   * @returns {Promise<{success: boolean, data: {account: object, isNew: boolean}}>}
   */
  createVirtualAccount: async (bvn, nin) => {
    const response = await axios.post('/wallet/virtual-account/create', { bvn, nin });
    return response.data;
  },

  /**
   * Get user's virtual account details
   * 
   * @returns {Promise<{success: boolean, data: {account: object}}>}
   */
  getVirtualAccountDetails: async () => {
    const response = await axios.get('/wallet/virtual-account/details');
    return response.data;
  },

  /**
   * Get virtual account transaction history
   * 
   * @param {object} params - Query parameters
   * @param {string} params.from - Start date (ISO string)
   * @param {string} params.to - End date (ISO string)
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise<{success: boolean, data: {transactions: array, pagination: object}}>}
   */
  getVirtualAccountTransactions: async (params = {}) => {
    const response = await axios.get('/wallet/virtual-account/transactions', { params });
    return response.data;
  },

  /**
   * Deactivate virtual account
   * 
   * @returns {Promise<{success: boolean, message: string}>}
   */
  deactivateVirtualAccount: async () => {
    const response = await axios.delete('/wallet/virtual-account');
    return response.data;
  },

  // ============================================
  // KYC METHODS
  // ============================================

  /**
   * Update user's KYC information (BVN/NIN)
   * 
   * @param {string} bvn - 11-digit BVN (optional)
   * @param {string} nin - 11-digit NIN (optional)
   * @returns {Promise<{success: boolean, data: {bvnVerified: boolean, ninVerified: boolean}}>}
   */
  updateKYC: async (bvn, nin) => {
    const response = await axios.put('/wallet/kyc', { bvn, nin });
    return response.data;
  },

  /**
   * Get user's KYC verification status
   * 
   * @returns {Promise<{success: boolean, data: {bvnVerified: boolean, ninVerified: boolean, hasBvn: boolean, hasNin: boolean}}>}
   */
  getKYCStatus: async () => {
    const response = await axios.get('/wallet/kyc/status');
    return response.data;
  },
};

export default walletAPI;